import { useMemo } from 'react';

import _ from 'lodash';
import { useIntl } from 'umi';

import { splitHeroDetailFields } from '@/components/HeroDetails';
import { CONFIG_FORM_VIEW, getConfigFormHeroSummary, getDetails } from '@/utils/infrastructure';

/**
 * Read-only ConfigFormDrawer details (no CRUD / useConfigForm).
 * Fetch stays in the caller — this only maps the loaded item into hero fields.
 */
export function useConfigDetails({
  open,
  selectedId,
  item,
  loading,
  formConfig,
  resourceKey,
  transformDetails,
}) {
  const { formatMessage } = useIntl();

  const isCurrentDetail =
    !_.isNil(item) && selectedId != null && String(item.id) === String(selectedId);

  const details = useMemo(() => {
    if (!isCurrentDetail) return [];
    const rows = getDetails(formConfig, {}, item);
    return transformDetails ? transformDetails(rows, { item, formatMessage }) : rows;
  }, [isCurrentDetail, formConfig, item, transformDetails, formatMessage]);

  const { mainFields: mainDetailFields, auditFields: auditDetailFields } = useMemo(
    () => splitHeroDetailFields(details),
    [details],
  );

  const drawerSummary = useMemo(
    () =>
      getConfigFormHeroSummary(formatMessage, {
        mode: CONFIG_FORM_VIEW.DETAILS,
        resourceKey,
      }),
    [formatMessage, resourceKey],
  );

  const isDetailsLoading =
    Boolean(loading) || (open && selectedId != null && !isCurrentDetail && loading == null);

  return {
    isCurrentDetail,
    isDetailsLoading,
    drawerSummary,
    mainDetailFields,
    auditDetailFields,
  };
}

export default useConfigDetails;
